import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { isNotNull } from '@/utils/helpers';
import { RootState } from '../../store';
import {
    postAdminQuizSharedV2List,
    AdminQuizSharedV2ListRequestBody,
} from '@/features/Quiz/api/getQuizzes';
import { QuizSharedByAdminV2Row } from '@/features/Quiz/types';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';

const PAGE_SIZE = 20;

const selectSharedRowId = (row: QuizSharedByAdminV2Row) =>
    `${row.quizId}-${row.sharedWith}-${row.sharedAt ?? '0'}`;

export const getAllQuizzesSharedByAdminV2 = createAsyncThunk<
    { items: QuizSharedByAdminV2Row[]; page: number; total: number; totalPages: number },
    {
        activePage: number;
        activeSearchKeyword?: string;
    },
    { rejectValue: string }
>('quizzes/getAllQuizzesSharedByAdminV2', async ({ activePage, activeSearchKeyword }) => {
    const body: AdminQuizSharedV2ListRequestBody = {
        page: activePage - 1,
        size: PAGE_SIZE,
        sort: [['sharedAt', 'desc']],
        ...(activeSearchKeyword?.trim() ? { title: activeSearchKeyword.trim() } : {}),
    };
    const res = await postAdminQuizSharedV2List(body);
    const totalPages = res.size > 0 ? Math.max(1, Math.ceil(res.total / res.size)) : 1;

    return {
        items: res.data ?? [],
        page: activePage,
        total: res.total,
        totalPages,
    };
});

const quizSharedByAdminV2Adapter = createEntityAdapter<QuizSharedByAdminV2Row>({
    selectId: selectSharedRowId,
});

type State = {
    pages: Record<number, string[]>;
    activePage: number;
    total: number;
    totalPages: number;
    isLoading: boolean;
};

const quizSharedByAdminV2Slice = createSlice({
    name: 'quizSharedByAdminV2',
    initialState: quizSharedByAdminV2Adapter.getInitialState<State>({
        pages: {},
        activePage: 1,
        totalPages: 1,
        total: 0,
        isLoading: false,
    }),
    reducers: {
        setQuizSharedByAdminV2ActivePage: (state, action: PayloadAction<number>) => {
            state.activePage = action.payload;
        },
        resetQuizSharedByAdminV2Pages: (state) => {
            state.pages = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAllQuizzesSharedByAdminV2.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(getAllQuizzesSharedByAdminV2.fulfilled, (state, action) => {
            quizSharedByAdminV2Adapter.upsertMany(state, action.payload.items);
            state.pages[action.payload.page] = action.payload.items.map(selectSharedRowId);
            state.total = action.payload.total;
            state.activePage = action.payload.page;
            state.totalPages = action.payload.totalPages;
            state.isLoading = false;
        });
        builder.addCase(getAllQuizzesSharedByAdminV2.rejected, (state) => {
            state.isLoading = false;
        });
    },
});

export const quizSharedByAdminV2 = quizSharedByAdminV2Slice.reducer;
export const { resetQuizSharedByAdminV2Pages, setQuizSharedByAdminV2ActivePage } =
    quizSharedByAdminV2Slice.actions;

const selectState = (state: RootState) => state.quizzes.quizSharedByAdminV2;

export const { selectById: selectQuizSharedByAdminV2ById } = quizSharedByAdminV2Adapter.getSelectors(selectState);

const selectRootState = (state: RootState) => state;

const selectIdsByPage = createSelector(
    [selectState, (_: RootState, page: number) => page],
    (state, page) => state.pages[page] ?? []
);

export const selectQuizSharedByAdminV2Page = createSelector(
    [selectRootState, (state: RootState, page: number) => selectIdsByPage(state, page)],
    (state, ids) => ids.map((id) => selectQuizSharedByAdminV2ById(state, id)).filter(isNotNull)
);

export const selectQuizSharedByAdminV2ActivePage = createSelector([selectState], (state) => state.activePage);
export const selectQuizSharedByAdminV2TotalPages = createSelector([selectState], (state) => state.totalPages);
export const selectQuizSharedByAdminV2Loading = createSelector([selectState], (state) => state.isLoading);
